import {
    CapacityValue,
    CountedRegulation,
    CountsAlgorithm,
    CountType,
    FLIGHT_LIST_BY_A_O_REQUEST_DTO_CLAZZ_VALUE,
    FLIGHT_LIST_BY_AERODROME_REQUEST_DTO_CLAZZ_VALUE,
    FLIGHT_LIST_BY_AIRSPACE_REQUEST_DTO_CLAZZ_VALUE,
    FLIGHT_LIST_BY_POINT_REQUEST_DTO_CLAZZ_VALUE,
    FLIGHT_LIST_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
    FlightDto,
    FlightListAlgorithm,
    FlightListByAerodromeRequestDto,
    FlightListByAirspaceRequestDto,
    FlightListByAORequestDto,
    FlightListByLocationRequestDto,
    FlightListByPointRequestDto,
    FlightListByTvRequestDto,
    FlightModelCounts,
    HotspotId,
    LoadClassification,
    MonitoredLoadValue,
    NM_TIME_SLICE_COUNT_DATA_CLAZZ_VALUE,
    NmTimeSliceCountData,
    Otmv,
    OtmvAlert_AlertStatus,
    OtmvPlanDto,
    PlanDataSourceType,
    PROBABILISTIC_TIME_SLICE_COUNTS_DATA_CLAZZ_VALUE,
    ProbabilisticTimeSliceCountsData,
    ProbabilityAtTimes,
    PSEUDO_NM_TIME_SLICE_COUNT_DATA_CLAZZ_VALUE,
    PseudoNmTimeSliceCountData,
    RegulationSubPeriod,
    TIME_SLICE_STANDARD_REGULATION_SUB_COUNTS_CLAZZ_VALUE,
    TIME_SLICE_STANDARD_SUB_COUNTS_CLAZZ_VALUE,
    TimePeriod,
    TimeSliceCountData,
    TimeSliceStandardRegulationSubCounts,
    TimeSliceStandardSubCounts,
    TimeSliceSubCounts,
    TimeWindowType,
    TRAFFIC_COUNTS_BY_AERODROME_REQUEST_DTO_CLAZZ_VALUE,
    TRAFFIC_COUNTS_BY_AIRLINE_REQUEST_DTO_CLAZZ_VALUE,
    TRAFFIC_COUNTS_BY_AIRSPACE_REQUEST_DTO_CLAZZ_VALUE,
    TRAFFIC_COUNTS_BY_POINT_REQUEST_DTO_CLAZZ_VALUE,
    TRAFFIC_COUNTS_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
    TrafficCountsByAerodromeRequestDto,
    TrafficCountsByAirlineRequestDto,
    TrafficCountsByAirspaceRequestDto,
    TrafficCountsByPointRequestDto,
    TrafficCountsByTvRequestDto,
    TrafficCountsDto,
    TrafficCountsRequestDto,
    TrafficType,
    WEIGHTED_OCCUPANCY_TIME_SLICE_COUNT_DATA_CLAZZ_VALUE,
    WeightedOccupancyTimeSliceCountData,
} from 'app/model/CORE';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import FlightUtils from 'app/common/util/FlightUtils';
import TimeRangeUtils from 'app/common/util/TimeRangeUtils';
import TimeUtils from 'app/common/util/TimeUtils';
import B2BLegacyUtils from './B2BLegacyUtils';

export default class TrafficCountUtils {
    public static readonly UNLIMITED_CAPACITY_VALUE = 999;
    public static readonly NO_CAPACITY_VALUE = -1;

    public static readonly UNUSED_OTMV_PEAK_SUSTAINED_VALUE = -1;

    /**
     * We consider OTMVs with sustainedElapsed or occurence of 99 as unused.
     * This is not technically the case within the NM system, but for NPPs own counts engine it implies a heavy burden
     * (i.e. retrieving an extra 1h39 of extra data).
     * Operationally, I suspect FMPs specify 99 to ignore the sustained alerts.
     */
    public static readonly UNUSED_SUSTAINED_OCCURENCE_ELAPSED_VALUE = 99;

    static isOtmvPeakUsed(otmv: Otmv) {
        return otmv.peakValue !== TrafficCountUtils.UNUSED_OTMV_PEAK_SUSTAINED_VALUE;
    }
    static isOtmvSustainedUsed(otmv: Otmv): boolean {
        return otmv.sustainedValue !== TrafficCountUtils.UNUSED_OTMV_PEAK_SUSTAINED_VALUE;
    }

    static isOtmvSustainedElapsedUsed(otmv: Otmv): boolean {
        return (
            otmv.sustainedElapseInMinutes !== TrafficCountUtils.UNUSED_SUSTAINED_OCCURENCE_ELAPSED_VALUE &&
            otmv.sustainedOccurence !== TrafficCountUtils.UNUSED_SUSTAINED_OCCURENCE_ELAPSED_VALUE
        );
    }

    static getActiveOtmv(otmvs: Array<Otmv>, time: Date): Otmv {
        return otmvs ? otmvs.find(otmv => TimePeriodUtils.contains(otmv.timePeriod, time)) : null;
    }

    static getOtmvNmValues(otmvPlan: OtmvPlanDto): Otmv[] {
        if (otmvPlan) {
            return otmvPlan.otmvPlans.flatMap(o => o.nmValues);
        }
    }

    static getOtmvClientValues(otmvPlan: OtmvPlanDto): Otmv[] {
        if (otmvPlan) {
            return otmvPlan.otmvPlans.flatMap(o => o.clientValues);
        }
    }

    static getCountsFor(dto: TrafficCountsDto, ttype: TrafficType): FlightModelCounts {
        switch (ttype) {
            case TrafficType.DEMAND:
                return dto.demandCounts;
            case TrafficType.LOAD:
                return dto.loadCounts;
            case TrafficType.REGULATED_DEMAND:
                return dto.regulatedCounts;
        }
        return undefined; // should never happen
    }

    /**
     * Return the lowest hourly capacity (entry rates or regulated rates) applicable during the given time Period.
     * Original Java method called getLowestHourlyCapacity : renamed due to overloading.
     * @param counts
     * @param tp
     */
    static getCountsLowestHourlyCapacity(counts: TrafficCountsDto, tp: TimePeriod) {
        let reply = -1;
        if (counts.hourlyEntryCapacity) {
            const cv = TrafficCountUtils.getLowestHourlyCapacity(counts.hourlyEntryCapacity, tp);
            if (cv?.hourlyCapacity !== TrafficCountUtils.NO_CAPACITY_VALUE) {
                reply = cv.hourlyCapacity;
            }
        }
        if (counts.regulatedPeriods) {
            const rp = TrafficCountUtils.getLowestRegSubPeriod(counts.regulatedPeriods, tp);
            if (rp) {
                const rate = TrafficCountUtils.getRegSubPeriodRate(rp);
                reply = reply === -1 ? rate : Math.min(reply, rate);
            }
        }
        return reply;
    }

    /**
     * Return the lowest hourly capacity applicable during the given time Period.
     */
    static getLowestHourlyCapacity(capacities: CapacityValue[], tp: TimePeriod): CapacityValue {
        let minValue = null;
        if (capacities) {
            for (const cv of capacities) {
                if (TimePeriodUtils.intersects(cv.timePeriod, tp)) {
                    if (minValue === null || cv.hourlyCapacity < minValue.hourlyCapacity) {
                        minValue = cv;
                    }
                }
            }
        }
        return minValue;
    }

    /**
     * Return the regulation sub-period with the lowest hourly capacity applicable during the given time Period.
     */
    static getLowestRegSubPeriod(regulations: CountedRegulation[], tp: TimePeriod): RegulationSubPeriod {
        if (!regulations) return null;
        let reply = null;
        let minValue = -1;
        for (const pur of regulations) {
            for (const rsp of pur.subPeriods) {
                if (TimePeriodUtils.intersects(rsp.period, tp)) {
                    const rate = TrafficCountUtils.getRegSubPeriodRate(rsp);
                    if (minValue === -1 || rate < minValue) {
                        minValue = rate;
                        reply = rsp;
                    }
                }
            }
        }
        return reply;
    }

    /**
     * Return the rate (combination of equipment / normal /pending rates)
     * @param rsp
     */
    static getRegSubPeriodRate(rsp: RegulationSubPeriod) {
        return rsp.equipmentRate + rsp.normalRate + rsp.pendingRate;
    }

    static isProbabilisticCount(item: TimeSliceCountData): item is ProbabilisticTimeSliceCountsData {
        return item.clazz === PROBABILISTIC_TIME_SLICE_COUNTS_DATA_CLAZZ_VALUE;
    }

    static isNmCount(item: TimeSliceCountData): item is NmTimeSliceCountData {
        return item.clazz === NM_TIME_SLICE_COUNT_DATA_CLAZZ_VALUE;
    }

    static isPseudoNmCount(item: TimeSliceCountData): item is PseudoNmTimeSliceCountData {
        return item.clazz === PSEUDO_NM_TIME_SLICE_COUNT_DATA_CLAZZ_VALUE;
    }

    static isWeightedCount(item: TimeSliceCountData): item is WeightedOccupancyTimeSliceCountData {
        return item.clazz === WEIGHTED_OCCUPANCY_TIME_SLICE_COUNT_DATA_CLAZZ_VALUE;
    }

    static isStandardSubCounts(item: TimeSliceSubCounts): item is TimeSliceStandardSubCounts {
        return item.clazz === TIME_SLICE_STANDARD_SUB_COUNTS_CLAZZ_VALUE;
    }

    static isStandardRegulationSubCounts(item: TimeSliceSubCounts): item is TimeSliceStandardRegulationSubCounts {
        return item.clazz === TIME_SLICE_STANDARD_REGULATION_SUB_COUNTS_CLAZZ_VALUE;
    }

    /**
     * Returns a value from [0.0, 1.0] indicating the flight's contribution to the count bar 'idx'.
     * For normal (NM/ Pseudo NM) counts the contribution will be binary - either 0.0 or 1.0.
     * However, for probabilistic and complexity counts, the value will be variable, depending on the computed contribution.
     *
     */
    static getCountPeriodContribution(
        counts: TrafficCountsDto,
        ttype: TrafficType,
        idx: number,
        flight: FlightDto,
    ): number {
        const tscd = TrafficCountUtils.getCountsFor(counts, ttype).counts[idx];
        if (TrafficCountUtils.isWeightedCount(tscd)) {
            const winfo = tscd.weightedInfo;
            const fInfo =
                winfo?.flightInfos &&
                winfo?.flightInfos.find(fi => FlightUtils.isSameFlight(flight.flightId, fi.flightId));
            return fInfo ? fInfo.weight : 0.0;
        } else if (TrafficCountUtils.isProbabilisticCount(tscd)) {
            // TODO : move to new pattern where the data is in the counts structure!!
            if (flight.probabilityInCountSteps) {
                // = have probabilistic data, and hence cannot use the normal NM entry exit times.
                // = all we have are the count steps - so we see if overlap.
                return TrafficCountUtils.getProbabilityAtTime(
                    flight.probabilityInCountSteps,
                    TimeRangeUtils.getTimeAtStep(counts.timeRange, idx),
                );
            } else {
                return TrafficCountUtils.isInNmCountingPeriod(
                    TimeRangeUtils.getCountingPeriodAtStep(counts.timeRange, idx),
                    counts.countType,
                    flight,
                )
                    ? 1.0
                    : 0.0;
            }
        } else {
            return TrafficCountUtils.isInNmCountingPeriod(
                TimeRangeUtils.getCountingPeriodAtStep(counts.timeRange, idx),
                counts.countType,
                flight,
            )
                ? 1.0
                : 0.0;
        }
    }

    /**
     * This method will compute the flight's contribution to the count bar 'i', but applying first the given
     * deltaShiftInMs to the flight.
     * This is typically needed in interactive HMIs where the FMP wishes to see the immediate effect of a
     * cherry picking ground delay on a flight.
     *
     * Note : if this deltaShift goes beyond the edges of the traffic counts range, then the
     *        weighted occupancy approach is not able to compute a correct value (since we do not have the data).
     *        In this case, we revert to the standard NM 1.0/0.0 behaviour.
     * @param counts
     * @param ttype
     * @param idx
     * @param flight
     * @param deltaShiftInMs
     */
    static getCountPeriodContributionWithDeltaShift(
        counts: TrafficCountsDto,
        ttype: TrafficType,
        idx: number,
        flight: FlightDto,
        deltaShiftInMs: number,
    ): number {
        const countTimePeriod = TimeRangeUtils.getCountingPeriodAtStep(counts.timeRange, idx);
        let tscd = TrafficCountUtils.getCountsFor(counts, ttype).counts[idx];
        if (TrafficCountUtils.isWeightedCount(tscd)) {
            const comingFromIdx = TimeRangeUtils.getStep(
                counts.timeRange,
                TimeUtils.substractMillis(countTimePeriod.from, deltaShiftInMs),
            );
            if (TimeRangeUtils.isStepInRange(counts.timeRange, comingFromIdx)) {
                tscd = TrafficCountUtils.getCountsFor(counts, ttype).counts[comingFromIdx];
                const winfo = (tscd as WeightedOccupancyTimeSliceCountData).weightedInfo;
                const fInfo =
                    winfo?.flightInfos &&
                    winfo?.flightInfos.find(fi => FlightUtils.isSameFlight(flight.flightId, fi.flightId));
                return fInfo ? fInfo.weight : 0.0;
            } else {
                // = the time shift has caused us to go beyond the available data ...
                // = so what to do?
                // = for the moment we fall back upon the default NM logic of 1.0/0.0
                const from = TimeUtils.addMillis(flight.timeAtReferenceLocationEntry.time, deltaShiftInMs);
                const to = flight.timeAtReferenceLocationExit
                    ? TimeUtils.addMillis(flight.timeAtReferenceLocationExit.time, deltaShiftInMs)
                    : undefined;
                return TrafficCountUtils.isInNmCountingPeriod_(countTimePeriod, counts.countType, from, to) ? 1.0 : 0.0;
            }
        } else if (TrafficCountUtils.isProbabilisticCount(tscd)) {
            // TODO : move to new pattern where the data is in the counts structure!!
            if (flight.probabilityInCountSteps) {
                // = have probabilistic data, and hence cannot use the normal NM entry exit times.
                // = all we have are the count steps - so we see if overlap.
                return TrafficCountUtils.getProbabilityAtTime(
                    flight.probabilityInCountSteps,
                    TimeUtils.substractMillis(countTimePeriod.from, deltaShiftInMs),
                );
            } else {
                const from = TimeUtils.addMillis(flight.timeAtReferenceLocationEntry.time, deltaShiftInMs);
                const to = flight.timeAtReferenceLocationExit
                    ? TimeUtils.addMillis(flight.timeAtReferenceLocationExit.time, deltaShiftInMs)
                    : undefined;
                return TrafficCountUtils.isInNmCountingPeriod_(countTimePeriod, counts.countType, from, to) ? 1.0 : 0.0;
            }
        } else {
            const from = TimeUtils.addMillis(flight.timeAtReferenceLocationEntry.time, deltaShiftInMs);
            const to = flight.timeAtReferenceLocationExit
                ? TimeUtils.addMillis(flight.timeAtReferenceLocationExit.time, deltaShiftInMs)
                : undefined;
            return TrafficCountUtils.isInNmCountingPeriod_(countTimePeriod, counts.countType, from, to) ? 1.0 : 0.0;
        }
    }

    /**
     * Determine if a flight's entry/exit time place it within a single count bar
     * defined by the countTimePeriod [FROM, TO[
     *
     * For ENTRY counts, the flight's entry time must be between [FROM, TO[
     *
     * For OCCUPANCY counts, the flight must be in the TV.
     * So, flight exit > [FROM   (i.e. a flight exiting at 10:00.01 is included in the 10:00 count bar) (*)
     *     flight entry < TO[     (i.e. a flight entering at 10:10 is not included in the [10:00, 10:10[ bar)
     *
     * (*) strictly speaking, a flight is is the bar if it exits at 10:00.01 ... 10:00.59
     *     In releases before R26, the entry/exit times have HH:MM resolution.
     *     Therefore, an exit time of 10:00 has a 59/60 chance of being in the bar (so we test for >= [FROM)
     *
     * This method detects the presence of probabilistic data, and will use it, if present
     * (So the caller should ensure consistency of this data with the countType parameter passed in).
     *
     * @param countBarPeriod
     * @param countType
     * @param flight
     * @return
     */
    static isInCountBarPeriod(countBarPeriod: TimePeriod, countType: CountType, flight: FlightDto): boolean {
        if (flight.probabilityInCountSteps) {
            // = have probabilistic data, and hence cannot use the normal NM entry exit times.
            // = all we have are the count steps - so we see if overlap.
            return TrafficCountUtils.getProbabilityAtTime(flight.probabilityInCountSteps, countBarPeriod.from) > 0;
            //			TimePeriod probCountSteps = flight.getProbabilityInCountSteps().getTimePeriod();
            //			return countTimePeriod.intersects(probCountSteps);
        } else {
            // = apply the usual NM entry/exit time logic
            return TrafficCountUtils.isInNmCountingPeriod(countBarPeriod, countType, flight);
        }
    }
    /**
     * Determine if a flight's entry/exit time place it within the countTimePeriod [FROM, TO[.
     * This method uses just the NM logic (no probability functionality).
     */
    static isInNmCountingPeriod(countTimePeriod: TimePeriod, countType: CountType, flight: FlightDto): boolean {
        return TrafficCountUtils.isInNmCountingPeriod_(
            countTimePeriod,
            countType,
            flight.timeAtReferenceLocationEntry.time,
            flight.timeAtReferenceLocationExit && flight.timeAtReferenceLocationExit.time,
        );
    }

    /**
     * Return true if the reference TrafficType includes the other.
     */
    static isTrafficTypeIncludes(reference: TrafficType, other: TrafficType): boolean {
        switch (reference) {
            case TrafficType.LOAD:
                return true;
            case TrafficType.REGULATED_DEMAND:
                return other !== TrafficType.LOAD;
            case TrafficType.DEMAND:
                return other === TrafficType.DEMAND;
        }
        return undefined;
    }
    /**
     * Determine if a flight's entry/exit time place it within the countTimePeriod [FROM, TO[
     *
     * For ENTRY counts, the flight's entry time must be between [FROM, TO[
     *
     * For OCCUPANCY counts, the flight must be in the TV.
     * So, flight exit > [FROM   (i.e. a flight exiting at 10:00.01 is included in the 10:00 count bar) (*)
     *     flight entry < TO[     (i.e. a flight entering at 10:10 is not included in the [10:00, 10:10[ bar)
     *
     * (*) strictly speaking, a flight is is the bar if it exits at 10:00.01 ... 10:00.59
     *     In releases before R26, the entry/exit times have HH:MM resolution.
     *     Therefore, an exit time of 10:00 has a 59/60 chance of being in the bar (so we test for >= [FROM)
     */
    private static isInNmCountingPeriod_(
        countTimePeriod: TimePeriod,
        countType: CountType,
        entry: Date,
        exit: Date,
    ): boolean {
        switch (countType) {
            case CountType.ENTRY:
                return TimePeriodUtils.contains(countTimePeriod, entry);
            case CountType.OCCUPANCY:
                if (B2BLegacyUtils.isFlightListEntryExitTimeWithSeconds()) {
                    // flight entry/exit times are HH:MM:SS resolution, so no more precision errors.
                    return TimePeriodUtils.intersectsDates(countTimePeriod, entry, exit);
                } else {
                    // = be careful : the exit time is INCLUSIVE (i.e. a flight exiting at 10:00 is included in the 10:00 bar)
                    return (
                        exit.getTime() >= countTimePeriod.from.getTime() &&
                        countTimePeriod.to.getTime() > entry.getTime()
                    );
                }
        }
        return undefined; // should never happen
    }

    static getProbabilityAtTime(pat: ProbabilityAtTimes, time: Date) {
        const idx = (time.getTime() - pat.startTime.getTime()) / pat.stepInMs;
        return idx >= 0 && idx < pat.probabilities.length ? pat.probabilities[idx] : 0.0;
    }

    static isTrafficCountsByTvRequest(request: TrafficCountsRequestDto): request is TrafficCountsByTvRequestDto {
        return request.clazz === TRAFFIC_COUNTS_BY_TV_REQUEST_DTO_CLAZZ_VALUE;
    }

    static isTrafficCountsByAirlineRequest(
        request: TrafficCountsRequestDto,
    ): request is TrafficCountsByAirlineRequestDto {
        return request.clazz === TRAFFIC_COUNTS_BY_AIRLINE_REQUEST_DTO_CLAZZ_VALUE;
    }

    static isTrafficCountsByAerodromeRequest(
        request: TrafficCountsRequestDto,
    ): request is TrafficCountsByAerodromeRequestDto {
        return request.clazz === TRAFFIC_COUNTS_BY_AERODROME_REQUEST_DTO_CLAZZ_VALUE;
    }

    static isTrafficCountsByAirspaceRequest(
        request: TrafficCountsRequestDto,
    ): request is TrafficCountsByAirspaceRequestDto {
        return request.clazz === TRAFFIC_COUNTS_BY_AIRSPACE_REQUEST_DTO_CLAZZ_VALUE;
    }

    static isTrafficCountsByPointRequest(request: TrafficCountsRequestDto): request is TrafficCountsByPointRequestDto {
        return request.clazz === TRAFFIC_COUNTS_BY_POINT_REQUEST_DTO_CLAZZ_VALUE;
    }

    static getTimeWindowType(countType: CountType): TimeWindowType {
        switch (countType) {
            case CountType.ENTRY:
                return TimeWindowType.ENTRY;
            case CountType.OCCUPANCY:
                return TimeWindowType.OCCUPANCY;
        }
    }

    /**
     * generate a Flight List Request that will retrieve all the flights counted by the traffic counts request, and
     * for the given traffic type.
     * Two types of request can be generated -
     * <li> Traffic Counts format - i.e. with a CountsInterval
     * <li> simple flight list format - with the [WEF, UNT[ representing the complete effective traffic window.
     *
     * The Simple flight list format is the default, with the CountsInterval only used for PROBABILISTIC flight lists.
     * @param tcRequest
     * @param ttype
     * @return
     */
    static generateFlightListRequestFor(
        tcRequest: TrafficCountsRequestDto,
        ttype: TrafficType,
    ): FlightListByLocationRequestDto {
        let reply: FlightListByLocationRequestDto = null;
        if (TrafficCountUtils.isTrafficCountsByTvRequest(tcRequest)) {
            reply = {
                clazz: FLIGHT_LIST_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
                //				aircraftOperators:null,
                //				flowId:null,
                timeType: TrafficCountUtils.getTimeWindowType(tcRequest.countType),
                tvName: tcRequest.tvId,
            } as FlightListByTvRequestDto;
        } else if (TrafficCountUtils.isTrafficCountsByAirlineRequest(tcRequest)) {
            reply = {
                clazz: FLIGHT_LIST_BY_A_O_REQUEST_DTO_CLAZZ_VALUE,
                aircraftOperators: [tcRequest.airline],
                timeType: TrafficCountUtils.getTimeWindowType(tcRequest.countType),
            } as FlightListByAORequestDto;
        } else if (TrafficCountUtils.isTrafficCountsByAerodromeRequest(tcRequest)) {
            reply = {
                clazz: FLIGHT_LIST_BY_AERODROME_REQUEST_DTO_CLAZZ_VALUE,
                aerodrome: tcRequest.aerodrome,
                role: tcRequest.role,
                //				aircraftOperators:null
            } as FlightListByAerodromeRequestDto;
        } else if (TrafficCountUtils.isTrafficCountsByAirspaceRequest(tcRequest)) {
            reply = {
                clazz: FLIGHT_LIST_BY_AIRSPACE_REQUEST_DTO_CLAZZ_VALUE,
                airspace: tcRequest.airspace,
                calculationType: TrafficCountUtils.getTimeWindowType(tcRequest.countType),
            } as FlightListByAirspaceRequestDto;
        } else if (TrafficCountUtils.isTrafficCountsByPointRequest(tcRequest)) {
            reply = {
                clazz: FLIGHT_LIST_BY_POINT_REQUEST_DTO_CLAZZ_VALUE,
                point: tcRequest.point,
                flightLevelRange: tcRequest.flightLevelRange,
            } as FlightListByPointRequestDto;
        }

        reply.dataset = tcRequest.dataset;
        //reply.fields = null;
        reply.proposalContexts = tcRequest.proposalContexts;
        reply.trafficType = ttype;
        reply.includeForecastFlights = tcRequest.includeForecastFlights;

        if (tcRequest.algorithm === CountsAlgorithm.PROBABILISTIC) {
            // = the probabilistic flight list algorithm needs to know the CountsInterval needed.
            //= therefore we resort to filling in the FList time period in the "traffic counts fashion".
            reply.algorithm = FlightListAlgorithm.PROBABILISTIC;
            reply.trafficWindow = tcRequest.trafficWindow;
            reply.countsInterval = { durationInMin: tcRequest.durationInMin, intervalInMin: tcRequest.intervalInMin };
        } else {
            // = we could use the countsInterval notation - but we do not.
            // = This is for two reasons :
            // =   CountsInterval has only been recently added to the Dto (June 2017), and there is a risk of regression in FlightSim facilities
            // =   More importantly, this counts interval format is not really digestible for the FMP. And we would not wish to fill a query panel in such a way.
            // =
            // = So, instead we prefer to compute directly the needed [WEF, UNT[
            reply.algorithm = FlightListAlgorithm.NM;
            reply.trafficWindow = TrafficCountUtils.countingPeriodFor(tcRequest);
        }

        return reply;
    }

    /**
     * Return the counting period (i.e. the time period in which flights will contribute)
     * @param tcRequest
     * @return
     */
    static countingPeriodFor(tcRequest: TrafficCountsRequestDto): TimePeriod {
        const tcTimePeriod = TrafficCountUtils.alignedTrafficWindowFor(tcRequest);
        const untCount = TimeUtils.addMinutes(tcTimePeriod.to, tcRequest.durationInMin - tcRequest.intervalInMin);
        const wefCount = TimeUtils.copy(tcTimePeriod.from);
        return { from: wefCount, to: untCount };
    }

    /**
     * Return the counting period (i.e. the time period in which flights will contribute)
     * @param timePeriod
     * @param intervalInMin
     * @param durationInMin
     */
    static countingPeriodFor2(timePeriod: TimePeriod, intervalInMin: number, durationInMin: number): TimePeriod {
        const tcTimePeriod = TrafficCountUtils.alignedTimePeriod(timePeriod, intervalInMin);
        const untCount = TimeUtils.addMinutes(tcTimePeriod.to, durationInMin - intervalInMin);
        const wefCount = TimeUtils.copy(tcTimePeriod.from);
        return { from: wefCount, to: untCount };
    }

    /**
     * Return a time period that is aligned on the natural boundaries of the traffic counts
     * i.e. for entry counts which are 20min interval, then the wef/unt values are xx:00, xx:20, xx:40 ..
     * @param tcRequest
     * @return
     */
    static alignedTrafficWindowFor(tcRequest: TrafficCountsRequestDto): TimePeriod {
        return TrafficCountUtils.alignedTimePeriod(tcRequest.trafficWindow, tcRequest.intervalInMin);
    }

    /**
     * Return a time period that is aligned on the natural boundaries of the traffic counts
     * i.e. for entry counts which are 20min interval, then the wef/unt values are xx:00, xx:20, xx:40 ..
     *
     * @param timePeriod
     * @param intervalInMinutes
     * @return
     */
    static alignedTimePeriod(timePeriod: TimePeriod, intervalInMinutes: number): TimePeriod {
        const wef = TimeUtils.nearestLowerMinuteBoundary(timePeriod.from, intervalInMinutes);
        let unt = TimeUtils.nearestUpperMinuteBoundary(timePeriod.to, intervalInMinutes);
        if (TimeUtils.diffInMs(unt, wef) > TimeUtils.DAY_TO_MS) {
            unt = TimeUtils.addDays(wef, 1);
        }
        return { from: wef, to: unt };
    }

    /**
     * Return a counts query [WEF, UNT[ time period that will cover the given INCLUSIVE time period, taking into account
     * the given duration and interval values.
     *
     * So, for example, if we have timePeriod=[13:16, 13:40] and interval=20 the result will be [12:20, 14:00[
     * thus ensuing that
     * <li> 13:16 is counted in the first count bar [12:20, 13:20[
     * <li> 13:40 is counted in the last count bar (13:20, 14:20(
     *
     * @param timePeriod
     * @param intervalInMinutes
     * @param durationInMinutes
     * @return
     */
    static alignedForInclusiveTimePeriod(
        timePeriod: TimePeriod,
        intervalInMinutes: number,
        durationInMinutes: number,
    ): TimePeriod {
        const start = TimeUtils.addMinutes(timePeriod.from, -durationInMinutes + intervalInMinutes);
        const wef = TimeUtils.nearestLowerMinuteBoundary(start, intervalInMinutes);
        let unt = TimeUtils.nearestUpperMinuteBoundary(timePeriod.to, intervalInMinutes);
        if (unt.getTime() === timePeriod.to.getTime()) {
            unt = TimeUtils.addMinutes(unt, intervalInMinutes);
        }
        if (TimeUtils.diffInMs(unt, wef) > TimeUtils.DAY_TO_MS) {
            unt = TimeUtils.addDays(wef, 1);
        }
        return { from: wef, to: unt };
    }

    /**
     * Return the flight list algorithm appropriate for the given Counts Algorithm.
     *
     * @param countAlgorithm
     * @return
     */
    static getFlightListAlgorithmFor(countAlgorithm: CountsAlgorithm): FlightListAlgorithm {
        switch (countAlgorithm) {
            case CountsAlgorithm.DEFAULT:
            case CountsAlgorithm.NM:
            case CountsAlgorithm.PSEUDO_NM:
            case CountsAlgorithm.WEIGHTED:
                return FlightListAlgorithm.NM;
            case CountsAlgorithm.PROBABILISTIC:
                return FlightListAlgorithm.PROBABILISTIC;
        }
        return null; // should never happen.
    }

    static getHighestCountsModel(dto: TrafficCountsDto): FlightModelCounts {
        if (dto.loadCounts) return dto.loadCounts;
        else if (dto.regulatedCounts) return dto.regulatedCounts;
        else return dto.demandCounts;
    }

    static toLoadClassificationString(value: LoadClassification) {
        if (value) {
            switch (value) {
                case LoadClassification.HIGH_THRESHOLD:
                    return 'High';
                case LoadClassification.NORMAL:
                    return 'Normal';
                case LoadClassification.LOW_THRESHOLD:
                    return 'Low';
                case LoadClassification.OVERLOAD:
                    return 'Overload';
                case LoadClassification.UNDEFINED:
                    return undefined;
            }
            return '??';
        }
    }

    static toLoadClassificationValue(value: LoadClassification) {
        if (value) {
            switch (value) {
                case LoadClassification.OVERLOAD:
                    return 4;
                case LoadClassification.HIGH_THRESHOLD:
                    return 3;
                case LoadClassification.LOW_THRESHOLD:
                    return 2;
                case LoadClassification.NORMAL:
                    return 1;
                case LoadClassification.UNDEFINED:
                    return 0;
            }
        }
        return -1;
    }

    static hasLoadClassificationAbove(
        loadValues: MonitoredLoadValue[],
        loadClassification: LoadClassification,
    ): boolean {
        if (loadValues) {
            const threshold = TrafficCountUtils.toLoadClassificationValue(loadClassification);
            for (const mlv of loadValues) {
                if (TrafficCountUtils.toLoadClassificationValue(mlv.loadClassification) > threshold) return true;
            }
        }
        return false;
    }

    static isAlertStatusHigherThan(value: OtmvAlert_AlertStatus, reference: OtmvAlert_AlertStatus) {
        if (value === reference) return false;
        return value === OtmvAlert_AlertStatus.PEAK; // only 2 values PEAK/SUSTAINED
    }

    /**
     * Returns the covered 'counting period' of the hotspot. That is, we include the duration value.
     * Descrption from Gregory De Pooter :
     *   a hotspot [16:00,16:30[ with duration 20 is mapped into counts:
     *   the last count "bar/period" is [16:29,16:49[ (as 16:30 is not included).
     *
     * @oaram hotspotId
     */
    static getHotspotCountingPeriod(hotspotId: HotspotId): TimePeriod {
        return {
            from: hotspotId.timePeriod.from,
            to: TimeUtils.addMinutes(hotspotId.timePeriod.to, hotspotId.durationInMinutes - 1),
        };
    }

    static getSubjectId(request: TrafficCountsRequestDto) {
        if (request) {
            if (TrafficCountUtils.isTrafficCountsByTvRequest(request)) {
                return request.tvId;
            } else if (TrafficCountUtils.isTrafficCountsByAerodromeRequest(request)) {
                return request.aerodrome;
            } else if (TrafficCountUtils.isTrafficCountsByAirlineRequest(request)) {
                return request.airline;
            } else if (TrafficCountUtils.isTrafficCountsByAirspaceRequest(request)) {
                return request.airspace;
            } else if (TrafficCountUtils.isTrafficCountsByPointRequest(request)) {
                return request.point;
            } else {
                return '???';
            }
        }
    }

    static getCountType(request: TrafficCountsRequestDto): CountType {
        if (request) {
            if (TrafficCountUtils.isTrafficCountsByTvRequest(request)) {
                return request.countType;
            } else if (TrafficCountUtils.isTrafficCountsByAerodromeRequest(request)) {
                return CountType.ENTRY;
            } else if (TrafficCountUtils.isTrafficCountsByAirlineRequest(request)) {
                return request.countType;
            } else if (TrafficCountUtils.isTrafficCountsByAirspaceRequest(request)) {
                return request.countType;
            } else if (TrafficCountUtils.isTrafficCountsByPointRequest(request)) {
                return CountType.ENTRY;
            }
        }
    }

    static calcCountsPanelTitle(request: TrafficCountsRequestDto, now?: Date) {
        return (
            TrafficCountUtils.getSubjectId(request) +
            (TrafficCountUtils.getCountType(request) === CountType.ENTRY ? ' ENTRY ' : ' OCC ') +
            request.intervalInMin +
            '/' +
            request.durationInMin +
            (now ? ' @ ' + TimeUtils.formatHHMMSS(now) : '')
        );
    }

    static getWeightedHourlyCapacity(capList: Array<CapacityValue>, tp: TimePeriod): CapacityValue {
        let hourlyCapacity = 0;
        let dataSource: PlanDataSourceType;
        if (capList) {
            capList.forEach(cv => {
                if (TimePeriodUtils.intersects(cv.timePeriod, tp)) {
                    const intersection = TimePeriodUtils.intersection(cv.timePeriod, tp);
                    hourlyCapacity +=
                        cv.hourlyCapacity *
                        ((TimePeriodUtils.getDurationInMs(intersection) * 1.0) / TimePeriodUtils.getDurationInMs(tp));
                    dataSource = cv.dataSource;
                }
            });
        }

        return {
            timePeriod: tp,
            hourlyCapacity: Math.round(Math.floor(hourlyCapacity)),
            dataSource: dataSource,
        };
    }
}
